import { type ReactNode } from 'react';
import { styled, useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Sidebar, Menu, MenuItem } from 'react-pro-sidebar';
import { menuLinks, socialLinks, type SocialLinkName } from '../../utils/consts.ts';
import logo from '../../assets/img/logo-with-text.svg';
import { useMobileSidebarStore } from '../../services/store';
import { fallbackFontFamilies } from '../../styles/consts.ts';
import { resetListStyles } from '../../styles/helpers.ts';
import { MobileButton } from '../buttons';
import { IconButton } from '../buttons/IconButton.tsx';
import { Cross, Dialog, Telegram, Twitter } from '../icons';
import { HelpMenu } from '../menus';

const MobileSidebarMenu = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const opened = useMobileSidebarStore(({ opened }) => opened);
  const close = useMobileSidebarStore(({ close }) => close);

  return (
    <Sidebar
      toggled={opened}
      breakPoint="always"
      backgroundColor="transparent"
      width="100%"
      rtl
      rootStyles={{
        ['&.ps-rtl']: {
          borderLeft: 'none',
        },
        ['.ps-sidebar-container']: {
          display: 'flex',
          flexDirection: 'column',
          background: theme.getColor('rgbaBlackBgColor'),
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          height: '100%',
        },
        ['.ps-sidebar-container::-webkit-scrollbar']: {
          display: 'none',
        },
      }}
    >
      <Header>
        <CloseButton color={'completelyWhite'} onClick={close}>
          <Cross />
        </CloseButton>
        <HeaderLogo src={logo} alt={'Logo'}></HeaderLogo>
      </Header>

      <Menu
        rootStyles={{
          paddingTop: 28,
          paddingLeft: 33,
          paddingRight: 33,
        }}
        menuItemStyles={{
          button: {
            height: 'auto',
            padding: '8px 0',
            fontSize: '24px',
            fontWeight: '600',
            fontFamily: `Inter, ${fallbackFontFamilies}`,
            textTransform: 'uppercase',
            transition: 'color 0.3s ease',
            color: '#ffffff',
            textAlign: 'left',
            '&:hover': {
              color: theme.getColor('accentSecondary'),
            },
          },
        }}
      >
        {menuLinks.map(({ label, href }) => (
          <MenuItem
            key={label}
            onClick={() => {
              window.open(href, '_blank');
              close();
            }}
          >
            {t(label)}
          </MenuItem>
        ))}
      </Menu>
      <Footer>
        <SocialLinks>
          {Object.entries(socialLinks).map(([label, href]) => (
            <SocialLinkItem key={label}>
              <IconButton
                href={href}
                target="_blank"
                rel={'noopener noreferrer'}
                noBorder
                bgColor={'completelyBlack'}
                color={'completelyWhite'}
              >
                {socialLinksIcons[label as SocialLinkName]}
              </IconButton>
            </SocialLinkItem>
          ))}
        </SocialLinks>
        <HelpMenu asideOffsets={18}>
          <HelpButton color={'gallery'} textColor={'shark'}>
            <span>{t('DESKTOP_BETS__helpButton')}</span>
            <HelpButtonIcon />
          </HelpButton>
        </HelpMenu>
      </Footer>
    </Sidebar>
  );
};

const Header = styled.header`
  display: flex;
  flex-direction: column;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  margin-top: 82px;
  margin-left: 45px;
  margin-right: 45px;
  padding-bottom: 20px;
`;

const HeaderLogo = styled.img`
  width: 293px;
  max-width: 100%;
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: 54px;
  right: 21px;
`;

const gap = 8;

const Footer = styled.footer`
  display: flex;
  justify-content: center;
  gap: ${gap}px;
  margin-top: auto;
  margin-bottom: 27px;
  direction: ltr;
  align-items: stretch;
`;

const SocialLinks = styled.ul`
  ${resetListStyles};
  display: flex;
  direction: ltr;
  gap: ${gap}px;
`;

const SocialLinkItem = styled.li``;

const HelpButton = styled(MobileButton)`
  padding-left: 16px;
  padding-right: 16px;
  > span {
    display: flex;
    align-items: center;
    text-transform: none;
    letter-spacing: -0.1px;
  }
`;

const HelpButtonIcon = styled(Dialog)`
  margin-left: ${({ theme }) => theme.responsive(8)};
  position: relative;
  top: ${({ theme }) => theme.responsive(2)};
`;

const socialLinksIcons: { [key in SocialLinkName]: ReactNode } = {
  twitter: <Twitter />,
  telegram: <Telegram />,
};

export { MobileSidebarMenu };
